
import { useState } from 'react';
import { faqCategories } from '../../../mocks/faqData';

interface FaqItem {
  id: number;
  category: string;
  pregunta: string;
  respuesta: string;
}

interface FaqAccordionProps {
  items: FaqItem[];
  activeCategory: string;
  searchTerm: string;
}

const FaqAccordion = ({ items, activeCategory, searchTerm }: FaqAccordionProps) => {
  const [openIds, setOpenIds] = useState<Set<number>>(new Set([items[0]?.id]));

  const toggleFaq = (id: number) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filtered = items.filter((item) => {
    const matchCategory = activeCategory === 'todas' || item.category === activeCategory;
    const matchSearch =
      !searchTerm ||
      item.pregunta.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.respuesta.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategory && matchSearch;
  });

  const getCategoryLabel = (catId: string) => {
    return faqCategories.find((c) => c.id === catId)?.label || catId;
  };

  const grouped: Record<string, FaqItem[]> = {};
  if (activeCategory === 'todas' && !searchTerm) {
    filtered.forEach((item) => {
      if (!grouped[item.category]) grouped[item.category] = [];
      grouped[item.category].push(item);
    });
  }

  const showGrouped = activeCategory === 'todas' && !searchTerm;

  return (
    <section id="faq-content" className="py-20 px-6 lg:px-12 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {searchTerm && (
          <p className="text-gray-500 text-sm mb-6">
            {filtered.length} resultado{filtered.length !== 1 ? 's' : ''} para &ldquo;<strong>{searchTerm}</strong>&rdquo;
          </p>
        )}

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No se encontraron resultados</h3>
            <p className="text-gray-500">Prueba con otros t\u00e9rminos o contacta con nosotros directamente</p>
            <a
              href="tel:+34936622445"
              className="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold mt-6 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              <span>Llamar: 936 622 445</span>
            </a>
          </div>
        )}

        {showGrouped
          ? Object.entries(grouped).map(([catId, catItems]) => (
              <div key={catId} className="mb-12">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <i className={`${faqCategories.find((c) => c.id === catId)?.icon} text-emerald-600`}></i>
                  <span>{getCategoryLabel(catId)}</span>
                </h3>
                <div className="space-y-3">
                  {catItems.map((faq) => (
                    <FaqItemCard
                      key={faq.id}
                      faq={faq}
                      isOpen={openIds.has(faq.id)}
                      onToggle={() => toggleFaq(faq.id)}
                    />
                  ))}
                </div>
              </div>
            ))
          : (
            <div className="space-y-3">
              {filtered.map((faq) => (
                <FaqItemCard
                  key={faq.id}
                  faq={faq}
                  isOpen={openIds.has(faq.id)}
                  onToggle={() => toggleFaq(faq.id)}
                  showCategory
                  categoryLabel={getCategoryLabel(faq.category)}
                />
              ))}
            </div>
          )}
      </div>
    </section>
  );
};

interface FaqItemCardProps {
  faq: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
  showCategory?: boolean;
  categoryLabel?: string;
}

const FaqItemCard = ({ faq, isOpen, onToggle, showCategory, categoryLabel }: FaqItemCardProps) => {
  return (
    <div
      className={`bg-white rounded-xl overflow-hidden border transition-all ${
        isOpen ? 'border-emerald-300 shadow-md shadow-emerald-50' : 'border-gray-200 hover:border-emerald-200'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 flex items-start justify-between text-left cursor-pointer"
      >
        <div className="flex-1 pr-4">
          {showCategory && categoryLabel && (
            <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full mb-2 inline-block">
              {categoryLabel}
            </span>
          )}
          <h4 className="text-base font-bold text-gray-900">
            <strong>{faq.pregunta}</strong>
          </h4>
        </div>
        <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-all ${
          isOpen ? 'bg-emerald-600 rotate-0' : 'bg-gray-100'
        }`}>
          <i className={`${isOpen ? 'ri-subtract-line text-white' : 'ri-add-line text-gray-600'} text-lg`}></i>
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-5 border-t border-gray-100 pt-4">
          <p className="text-gray-600 leading-relaxed text-sm">{faq.respuesta}</p>
        </div>
      </div>
    </div>
  );
};

export default FaqAccordion;
